/**
 * @file Patient Registration Page — /register/patient
 */
import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { AlertCircle } from "lucide-react";
import { authService } from "../services/auth";
import { useAuth } from "../hooks/useAuth";
import OAuthButtons from "../components/OAuthButtons";
import { AUTH_STRINGS as S } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, Input, Checkbox } from "../ui";

const schema = Yup.object({
  firstName: Yup.string().required(S.REQUIRED_FIELD),
  lastName: Yup.string().required(S.REQUIRED_FIELD),
  email: Yup.string().email(S.INVALID_EMAIL).required(S.REQUIRED_FIELD),
  acceptTerms: Yup.boolean().oneOf([true], S.ACCEPT_TERMS_REQUIRED).required(S.ACCEPT_TERMS_REQUIRED),
});

export default function PatientRegister() {
  const navigate = useNavigate();
  const { handleAuthSuccess } = useAuth();
  const [error, setError] = useState("");

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError("");
      await authService.patientRegister(values);
      navigate("/verify-code", { state: { email: values.email } });
    } catch (err) {
      setError(err.response?.data?.message || "Error al crear la cuenta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={S.PATIENT_REGISTER_TITLE} subtitle={S.PATIENT_REGISTER_SUBTITLE}>
      <p className="auth-oauth-consent">
        Al continuar con Google o Apple, aceptas nuestros{" "}
        <a href="https://consultto.mx/terminos" target="_blank" rel="noopener noreferrer">Términos</a>{" "}
        y el{" "}
        <a href="https://consultto.mx/aviso-de-privacidad" target="_blank" rel="noopener noreferrer">Aviso de Privacidad</a>.
      </p>
      <OAuthButtons
        role="PACIENTE"
        acceptTerms
        onSuccess={(data) => {
          handleAuthSuccess(data);
          navigate("/");
        }}
        onError={(msg) => setError(msg)}
      />
      <div className="auth-divider">o regístrate con tu correo</div>
      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      <Formik
        initialValues={{ firstName: "", lastName: "", email: "", acceptTerms: false }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
          <Form noValidate className="auth-form">
            <div className="auth-form-row">
              <Input
                name="firstName"
                label={S.PATIENT_FIRST_NAME}
                autoComplete="given-name"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.firstName && errors.firstName}
              />
              <Input
                name="lastName"
                label={S.PATIENT_LAST_NAME}
                autoComplete="family-name"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.lastName && errors.lastName}
              />
            </div>
            <Input
              name="email"
              type="email"
              label={S.LOGIN_EMAIL}
              autoComplete="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email}
            />
            <Checkbox
              checked={values.acceptTerms}
              onCheckedChange={(checked) => setFieldValue("acceptTerms", !!checked)}
              label={
                <span>
                  {S.ACCEPT_TERMS}{" "}
                  <a href="https://consultto.mx/terminos" target="_blank" rel="noreferrer" className="auth-link">
                    {S.TERMS_LINK}
                  </a>{" "}
                  {S.AND}{" "}
                  <a href="https://consultto.mx/aviso-de-privacidad" target="_blank" rel="noreferrer" className="auth-link">
                    {S.PRIVACY_LINK}
                  </a>
                </span>
              }
            />
            {touched.acceptTerms && errors.acceptTerms && (
              <span className="auth-checkbox-error">{errors.acceptTerms}</span>
            )}
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              {S.PATIENT_SUBMIT}
            </Button>
          </Form>
        )}
      </Formik>

      <div className="auth-links">
        <span className="auth-link-row">
          {S.PATIENT_HAS_ACCOUNT}{" "}
          <RouterLink to="/login/paciente" className="auth-link">
            {S.PATIENT_LOGIN}
          </RouterLink>
        </span>
      </div>
    </AuthShell>
  );
}
